import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blog';
import { useSEO } from '../../utils/seo';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function BlogPage() {
  useSEO({
    title: 'Roseville Blog | Local Guides, Tips & Community Stories | Roseville.org',
    description: 'Read the latest Roseville, CA guides, insider tips, and community stories. Discover local dining recommendations, neighborhood spotlights, event recaps, and relocation advice from Roseville residents.',
    keywords: 'Roseville blog, Roseville California guides, Roseville local tips, Roseville community stories, Roseville neighborhood guide, Placer County lifestyle',
    canonical: '/blog',
    ogImage: 'https://public.readdy.ai/ai/img_res/f66643671b652e275b56f8e0bcf285ee.jpg',
    author: 'Roseville.org Editorial Team',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Blog',
          name: 'Roseville.org Blog — Local Guides & Community Stories',
          description: 'Local guides, tips, and community stories about life in Roseville, California.',
          url: 'https://roseville.org/blog',
          inLanguage: 'en-US',
          publisher: {
            '@type': 'Organization',
            name: 'Roseville.org',
            url: 'https://roseville.org/',
            logo: {
              '@type': 'ImageObject',
              url: 'https://static.readdy.ai/image/daa4d1286c7e40add692fc5e2a0b9f5f/85b7f1e80330d554f03850e83485dc2a.jpeg',
              width: 512,
              height: 512,
            },
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://roseville.org/' },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://roseville.org/blog' },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 cursor-pointer">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">Roseville.org</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Home</Link>
              <Link to="/recreation" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Recreation</Link>
              <Link to="/real-estate" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Real Estate</Link>
              <Link to="/utilities" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Utilities</Link>
              <Link to="/blog" className="text-teal-600 font-semibold cursor-pointer">Blog</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-7xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl font-bold mb-4">News & Stories</h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto">
            Your source for local guides, community updates, and insider tips about life in Roseville
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                      {post.category}
                    </span>
                    {post.isSponsored && (
                      <span className="px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                        <i className="ri-advertisement-line"></i>
                        Sponsored
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {post.date}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-teal-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{post.author}</span>
                    </div>
                    <span className="text-teal-600 font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
                      Read More
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <MailchimpNewsletter />

      {/* Footer */}
      <footer className="bg-teal-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">Roseville.org</span>
            </div>
            <p className="text-gray-600 text-sm">
              © 2025 Roseville.org. All rights reserved. | <a href="https://readdy.ai/?ref=logo" className="text-teal-600 hover:underline cursor-pointer">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
